import {
  checkInventory,
  ControlPeriod,
  ControlPeriodType,
  FoodControlPoint,
  FoodManagementControls,
  getAllControlPeriods,
  getFoodControlPointsFromLocationId,
  useCommonStore,
} from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";

import { DishwasherTable } from "./DishwasherTable";
import { InventoryTable } from "./InventoryTable";
import { NonperiodicAndYearlyControlsTable } from "./NonperiodicAndYearlyControlsTable";
import { PeriodicControlPointsTable } from "./PeriodicControlPointsTable";

export function ControlPointsAndPeriods() {
  const { foodLocation: selectedLocationId } = useCommonStore();
  const { data: foodControlPoints, isPending: isControlPointsPending } =
    useQuery({
      queryKey: ["foodControlPoints", selectedLocationId],
      queryFn: () =>
        getFoodControlPointsFromLocationId(selectedLocationId ?? ""),
      enabled: !!selectedLocationId,
    });

  const { data: controlPeriods, isPending: isControlPeriodsPending } = useQuery(
    {
      queryKey: ["allControlPeriods"],
      queryFn: getAllControlPeriods,
      enabled: !!selectedLocationId,
    },
  );
  const isPending = isControlPointsPending || isControlPeriodsPending;
  type FilteredControls = {
    yearlyControls: FoodControlPoint[];
    periodicControls: FoodControlPoint[];
    inventoryControls: FoodControlPoint[];
    dishwashingControls: FoodControlPoint[];
  };

  const categorizeControlPoints = (
    foodControlPoints?: FoodControlPoint[],
    controlPeriods?: ControlPeriod[],
  ): FilteredControls => {
    return (
      foodControlPoints?.reduce<FilteredControls>(
        (acc, point) => {
          const periodType = controlPeriods?.find(
            (period) => period.id === point.controlPeriodId,
          )?.description as ControlPeriodType;

          if (checkInventory(point.type)) {
            acc.inventoryControls.push(point);
          } else if (point.type === FoodManagementControls.cleaningDishwasher) {
            acc.dishwashingControls.push(point);
          } else if (
            point.type === FoodManagementControls.cleaningFloor ||
            point.type === FoodManagementControls.cleaningHood ||
            periodType === "1 Year"
          ) {
            acc.yearlyControls.push(point);
          } else {
            acc.periodicControls.push(point);
          }

          return acc;
        },
        {
          yearlyControls: [],
          periodicControls: [],
          inventoryControls: [],
          dishwashingControls: [],
        },
      ) || {
        yearlyControls: [],
        periodicControls: [],
        inventoryControls: [],
        dishwashingControls: [],
      }
    );
  };
  const {
    yearlyControls,
    periodicControls,
    inventoryControls,
    dishwashingControls,
  } = categorizeControlPoints(foodControlPoints, controlPeriods);

  return (
    <div className="flex flex-col gap-[30px]">
      <InventoryTable
        isPending={isPending}
        foodControlPoints={inventoryControls}
        controlPeriods={controlPeriods ?? []}
      />
      <DishwasherTable
        isPending={isPending}
        foodControlPoints={dishwashingControls}
        controlPeriods={controlPeriods ?? []}
      />
      <PeriodicControlPointsTable
        isPending={isPending}
        foodControlPoints={periodicControls}
        controlPeriods={controlPeriods ?? []}
      />
      <NonperiodicAndYearlyControlsTable
        isPending={isPending}
        foodControlPoints={yearlyControls}
        controlPeriods={controlPeriods ?? []}
      />
    </div>
  );
}
